import { useState } from "react";
import { Form, Table, Collapse, Row, Col, Input, Button } from "reactstrap";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { GetFinalExamVariantsForCourse } from "../../actions/teacherQueries";
import Pacmanloader from "../../components/Pacmanloader/Pacmanloader";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage } from "react-intl";
import {
  faCheckCircle,
  faTimesCircle,
  faCaretDown,
  faCaretLeft,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import axios from "../../services/api/api";
import { toast } from "react-toastify";
import { useQueryClient } from "react-query";
import { injectIntl } from "react-intl";
import { TOAST_CONFIG } from "../../Utilities/toastConfig";

const CopyExamVariantForm = ({
  classId,
  course,
  student,
  level,
  otherClasses,
  dismissModal,
  intl,
}) => {
  const queryClient = useQueryClient();
  const animatedComponents = makeAnimated();

  const [selectedClassId, setSelectedClassId] = useState(null);
  const [openSelectedClassId, setOpenSelectedClassId] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [openExamId, setOpenExamId] = useState(null);
  const [collapsedExam, setCollapsedExam] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [targetVariantName, setTargetVariantName] = useState("");
  const [nameEmptyError, setNameEmptyError] = useState(false);
  const [nameAlreadyInUse, setNameAlreadyInUse] = useState(false);
  const [studentsCanRun, setStudentsCanRun] = useState(false);

  const finalExamVariants = GetFinalExamVariantsForCourse(classId, course?.id);
  const variantsNames = finalExamVariants?.data?.map((ex) =>
    ex.name.toLowerCase()
  );

  const classOptions = otherClasses.length
    ? otherClasses.map((c) => ({
        label: c.name,
        value: c.id,
      }))
    : [];

  const { data, refetch, isLoading } = GetFinalExamVariantsForCourse(
    selectedClassId,
    course?.id
  );

  if (isLoading) return <Pacmanloader />;

  const mappedStudentOptions = (students) => {
    return students
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((student) => ({
        label: student.name,
        value: student.studentId,
      }));
  };

  const studentOptions =
    level === "Student"
      ? [{ label: student[0].name, value: student[0].id }]
      : [
          { label: intl.formatMessage({ id: "SelectAll" }), value: "all" },
          ...mappedStudentOptions(course.students || []),
        ];

  const handleClassSelect = async (selectedClass) => {
    setSelectedClassId(selectedClass.value);
    setOpenSelectedClassId(selectedClass.value);
    await refetch();
    setSelectedExam(null);
  };

  const toggleCollapseExam = (exam) => {
    setOpenExamId((prev) => (prev === exam.id ? null : exam.id));
    setCollapsedExam(exam);
  };

  const handleStudentsSelect = (selectedOptions) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSelectedStudents([]);
      return;
    }

    const isSelectAll = selectedOptions.some((opt) => opt.value === "all");

    const updatedStudents = isSelectAll
      ? mappedStudentOptions(course.students)
      : selectedOptions.map((opt) => ({ value: opt.value, label: opt.label }));

    setSelectedStudents(updatedStudents);
  };

  const handleExamNameChange = (e) => {
    const value = e.target.value;
    setTargetVariantName(value);
    setNameEmptyError(false);

    if (value.trim() && variantsNames.includes(value.trim().toLowerCase())) {
      setNameAlreadyInUse(true);
    } else {
      setNameAlreadyInUse(false);
    }
  };

  const handleCopyFinalExamVariant = async () => {
    if (targetVariantName.trim().length === 0) {
      setNameEmptyError(true);
      return;
    }

    if (variantsNames.includes(targetVariantName.trim().toLowerCase())) {
      setNameAlreadyInUse(true);
      return;
    }

    const newExam = {
      sourceVariantId: selectedExam.id,
      toClassId: classId,
      targetVariantName: targetVariantName.trim(),
      forStudentIds: selectedStudents.map((s) => s.value),
      studentsCanRun,
    };

    const res = await axios
      .post("/Course/CopyFinalExamVariant", newExam, {
        headers: { "Content-Type": "application/json" },
      })
      .then(() => {
        toast.success(
          <FormattedMessage id="FinalExamCopySuccess" />,
          TOAST_CONFIG
        );
        dismissModal();

        queryClient.invalidateQueries([
          "FetchFinalExamVariantsForCourse",
          classId,
          course?.id,
        ]);
      })
      .catch((err) => {
        const errorMessage = err?.response?.data.trim();

        var toastMessage =
          errorMessage === "NameAlreadyInUse" ? (
            <FormattedMessage id="NameAlreadyInUse" />
          ) : errorMessage ? (
            errorMessage
          ) : (
            <FormattedMessage id="FinalExamCopyFail" />
          );

        toast.warn(toastMessage, TOAST_CONFIG);
      });

    return res;
  };

  const totalNumQuestions = selectedExam?.content?.reduce(
    (sum, c) => sum + (c.questionCount || 0),
    0
  );

  return (
    <>
      <Form className="m-1">
        <Row className="mb-3 align-items-center">
          <Col
            xs="auto"
            className="ms-2"
            onClick={() =>
              setOpenSelectedClassId((prev) =>
                prev === selectedClassId ? null : selectedClassId
              )
            }
          >
            <FontAwesomeIcon
              style={{ marginRight: ".5em" }}
              className={
                openSelectedClassId === selectedClassId
                  ? "fa-caret--animated"
                  : "fa-caret--animated collapsed"
              }
              icon={
                openSelectedClassId === selectedClassId
                  ? faCaretDown
                  : faCaretLeft
              }
            />
            <label className="fw-bold">
              <FormattedMessage id="FromClass" />
            </label>
          </Col>
          <Col>
            <Select
              closeMenuOnSelect
              components={animatedComponents}
              name="finalExamVariants"
              options={classOptions}
              onChange={handleClassSelect}
              styles={{
                menu: (base) => ({
                  ...base,
                  zIndex: 99,
                }),
              }}
              value={
                classOptions.find((opt) => opt.value === selectedClassId) ||
                null
              }
              placeholder={<FormattedMessage id="SelectClass" />}
            />
          </Col>
        </Row>

        <Collapse isOpen={openSelectedClassId === selectedClassId}>
          <div className="table-container">
            <Table className="copy-exam-head">
              <thead className="table-light">
                <tr>
                  <th></th>
                  <th></th>
                  <th>
                    <FormattedMessage id="Exam" />
                  </th>
                  <th>
                    <FormattedMessage id="CreatedDate" />
                  </th>
                  <th></th>
                </tr>
              </thead>
            </Table>

            <div className="copy-exam-body-wrapper mt-3">
              <Table className="copy-exam-body">
                <tbody>
                  {data?.length > 0 ? (
                    [...data]
                      .sort((a, b) => new Date(b.created) - new Date(a.created))
                      .map((exam) => {
                        return (
                          <>
                            <tr key={exam.id}>
                              <td style={{ paddingLeft: "15px" }}>
                                <input
                                  type="radio"
                                  name="examSelection"
                                  value={exam.id}
                                  style={{ cursor: "pointer" }}
                                  onChange={() => setSelectedExam(exam)}
                                />
                              </td>
                              <td
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleCollapseExam(exam)}
                              >
                                <FontAwesomeIcon
                                  style={{
                                    marginRight: ".5em",
                                    color: "#aaa",
                                  }}
                                  className={
                                    openExamId === exam.id
                                      ? "fa-caret--animated"
                                      : "fa-caret--animated collapsed"
                                  }
                                  icon={
                                    openExamId === exam.id
                                      ? faCaretDown
                                      : faCaretLeft
                                  }
                                />
                              </td>
                              <td>
                                {exam.isChildExam ? (
                                  <>
                                    {exam.name}
                                    <FontAwesomeIcon
                                      icon={faLayerGroup}
                                      className="ms-1 text-primary"
                                      title="Omprov"
                                    />
                                  </>
                                ) : (
                                  exam.name
                                )}
                              </td>
                              <td>
                                {moment(exam.created).format("YYYY-MM-DD")}
                              </td>
                              <td></td>
                            </tr>
                            <tr key={`${exam.id}-detail`}>
                              <td
                                colSpan="4"
                                style={{
                                  border: "none",
                                  paddingLeft: "15px",
                                }}
                              >
                                <Collapse isOpen={openExamId === exam.id}>
                                  <div
                                    style={{
                                      backgroundColor: "#f9f9f9",
                                      borderRadius: "0 0 8px 8px",
                                      padding: "10px 20px",
                                    }}
                                  >
                                    <Table borderless size="sm">
                                      <thead className="table-light">
                                        <tr>
                                          <th></th>
                                          <th></th>
                                          <th className="fw-medium text-start">
                                            <FormattedMessage id="Module" />
                                          </th>
                                          <th className="fw-medium text-end">
                                            <FormattedMessage id="QuestionCount" />
                                          </th>
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {collapsedExam?.content &&
                                          collapsedExam?.content.map(
                                            (item, i) => (
                                              <tr key={i}>
                                                <td></td>
                                                <td></td>
                                                <td className="text-start">
                                                  {
                                                    item.module
                                                      .nameCorrespondingFactModule
                                                  }
                                                </td>
                                                <td className="text-end">
                                                  {item.questionCount}
                                                </td>
                                              </tr>
                                            )
                                          )}
                                      </tbody>
                                    </Table>
                                  </div>
                                </Collapse>
                              </td>
                            </tr>
                          </>
                        );
                      })
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        <FormattedMessage id="NoExamsFound" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </Collapse>

        {selectedExam && (
          <>
            <div className="p-2 bg-white rounded shadow-sm mt-3">
              <h5 className="text-lg font-semibold">
                <FormattedMessage id="SelectedExam" />
                {selectedExam.name}
              </h5>

              <div className="bg-gray-100 p-2 rounded-lg text-sm border m-3 selected-exam-container">
                <div className="d-flex align-items-center">
                  <span className="me-2 text-muted">
                    <FormattedMessage id="NumberOfModules" />:
                  </span>
                  <span className="fst-italic">
                    {selectedExam.content.length}
                  </span>
                </div>

                <div className="d-flex align-items-center">
                  <span className="me-2 text-muted">
                    <FormattedMessage id="TotalNumQuestions" />:
                  </span>
                  <span className="fst-italic">{totalNumQuestions}</span>
                </div>

                <div className="d-flex align-items-center">
                  <span className="me-2 text-muted">
                    <FormattedMessage id="ApproveScoreLimit" />
                  </span>
                  <span className="fst-italic">
                    {selectedExam.approvalLimit}%
                  </span>
                </div>

                <div className="d-flex align-items-center">
                  <span className="me-2 text-muted">
                    <FormattedMessage id="ShowAnswerOptions" />
                  </span>
                  <FontAwesomeIcon
                    icon={selectedExam.numAlt ? faCheckCircle : faTimesCircle}
                    style={{
                      color: selectedExam.numAlt ? "#a3d9a5" : "#f5a3a3",
                    }}
                  />
                </div>
              </div>

              <Row className="mb-3">
                <Col
                  xs="auto"
                  style={{
                    width: "80px",
                  }}
                  className="mt-2"
                >
                  <label>
                    <FormattedMessage id="Name" />
                    <span className="text-danger">*</span>
                  </label>
                </Col>
                <Col>
                  <Input
                    name="name"
                    required
                    className="rounded w-100"
                    onChange={handleExamNameChange}
                    value={targetVariantName}
                  />
                  {(nameAlreadyInUse || nameEmptyError) && (
                    <div
                      className="text-danger"
                      style={{
                        fontSize: "0.9em",
                        marginTop: "2px",
                        width: "100%",
                      }}
                    >
                      {nameAlreadyInUse ? (
                        <FormattedMessage id="NameAlreadyInUse" />
                      ) : (
                        <FormattedMessage id="Views.Teacher.CreateAdminUser.Form.Error.RequireName" />
                      )}
                    </div>
                  )}
                </Col>
              </Row>

              <Row className="align-items-center mb-3">
                <Col xs="auto" style={{ width: "80px" }}>
                  <FormattedMessage id="Student" />
                </Col>
                <Col>
                  <Select
                    closeMenuOnSelect={false}
                    components={animatedComponents}
                    isMulti
                    name="students"
                    options={studentOptions}
                    onChange={handleStudentsSelect}
                    value={selectedStudents}
                    placeholder={<FormattedMessage id="ChooseStudents" />}
                  />
                </Col>
              </Row>
              <Row className="align-items-center mb-3">
                <Col xs="auto">
                  <Input
                    name="studentsCanRun"
                    style={{ cursor: "pointer", marginRight: "5px" }}
                    type="checkbox"
                    checked={studentsCanRun}
                    onChange={(e) => setStudentsCanRun(e.target.checked)}
                  />
                  <FormattedMessage id="StudentsCanRun" />
                </Col>
              </Row>
            </div>
          </>
        )}
      </Form>

      <div className="d-flex flex-row justify-content-end mt-4 me-1">
        <Button
          type="button"
          outline
          color="secondary"
          onClick={dismissModal}
          className="px-3 py-1 me-2"
        >
          <FormattedMessage id="Cancel" />
        </Button>

        <Button
          type="button"
          color="primary"
          outline
          className="px-3 py-1"
          onClick={handleCopyFinalExamVariant}
        >
          <FormattedMessage id="SaveAndClose" />
        </Button>
      </div>
    </>
  );
};

export default injectIntl(CopyExamVariantForm);
